import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation, Redirect } from "wouter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { 
  Settings, Building2, Users, Shield, CreditCard, 
  Edit2, Trash2, Loader2, Search, AlertCircle,
  BarChart3, FileText, UserPlus, UserMinus
} from "lucide-react";
import { useLanguage } from "@/components/language-provider";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { useState, useEffect } from "react";
import { API_BASE_URL } from "@/lib/config";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { format } from "date-fns";

interface Plan {
  id: string;
  planType: string;
  name: string;
  descriptionPt?: string;
  descriptionEn?: string;
  limitMessagePt?: string;
  limitMessageEn?: string;
  documentLimit: number;
  userLimit: number;
  /** "Ajustar com IA" in the model library, per user per month (0 = locked). */
  aiAdjustmentsPerMonth?: number;
  requiresCompany: boolean;
  priceMonthlyBrl: number;
  priceMonthlyUsd: number;
  priceAnnualBrl: number;
  priceAnnualUsd: number;
  isActive: boolean;
  contactSales?: boolean;
  features?: string;
  icon?: string;
  colorGradient?: string;
  popular?: boolean;
  stripePriceIdMonthly?: string;
  stripePriceIdAnnual?: string;
  includesSso?: boolean;
  includesDynamics?: boolean;
  includesApi?: boolean;
  includesBranding?: boolean;
}

interface Company {
  id: string;
  name: string;
  country: string;
  memberCount: number;
  plan: string;
  createdAt: string;
  isActive: boolean;
  deactivatedReason?: string;
  userLimit: number;
}

interface FreeUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  documentsUsed: number;
  createdAt: string;
}

interface AllUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  plan: string;
  role: string;
  isSuperAdmin: boolean;
  companyId?: string;
  companyName?: string;
  companyRole?: string;
  isDisabled: boolean;
  createdAt: string;
  documentsCount: number;
}

interface SuperAdmin {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  grantedAt: string;
}

interface PlatformStats {
  totalUsers: number;
  totalCompanies: number;
  totalDocuments: number;
  documentsThisMonth: number;
  documentsLastMonth: number;
  usersByPlan: {
    free: number;
    pro: number;
    enterprise: number;
    custom: number;
  };
  signatureStats: {
    pending: number;
    signed: number;
    declined: number;
  };
}

import { AppLayout } from "@/components/app-layout";
import { WhatsAppAgentCard } from "@/components/whatsapp-agent-card";
import { StripeStatusCard } from "@/components/stripe-status-card";


function SignupSettingsCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [intl, setIntl] = useState(true);
  const [br, setBr] = useState(false);
  const [phone, setPhone] = useState(false);
  const [showSignup, setShowSignup] = useState(true);
  const { toast } = useToast();

  const authHeaders = (): Record<string, string> => ({
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
    "Content-Type": "application/json",
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/system-admin/signup-settings`, { headers: authHeaders() });
        if (res.ok) {
          const d = await res.json();
          setIntl(!!d.requireCardInternational);
          setBr(!!d.requireCardBrazil);
          setPhone(!!d.requirePhoneVerification);
          setShowSignup(d.showFreeSignup !== false);
        }
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  const saveFlags = async (nextIntl: boolean, nextBr: boolean, nextPhone: boolean, nextShowSignup: boolean = showSignup) => {
    setIntl(nextIntl); setBr(nextBr); setPhone(nextPhone); setShowSignup(nextShowSignup); setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/system-admin/signup-settings`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({ requireCardInternational: nextIntl, requireCardBrazil: nextBr, requirePhoneVerification: nextPhone, showFreeSignup: nextShowSignup }),
      });
      if (!res.ok) throw new Error();
      toast({ title: "Configurações de cadastro salvas" });
    } catch {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="text-base">Cadastro — verificações de cadastro</CardTitle>
        <CardDescription>Exigir cartão de crédito (validado via Stripe, sem cobrança) antes do primeiro envio.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm">Exigir cartão — usuários internacionais (fora do Brasil)</span>
          <Switch className="shrink-0" checked={intl} disabled={loading || saving} onCheckedChange={(v) => saveFlags(v, br, phone)} />
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm">Exigir cartão — usuários do Brasil (além do CPF)</span>
          <Switch className="shrink-0" checked={br} disabled={loading || saving} onCheckedChange={(v) => saveFlags(intl, v, phone)} />
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm">Exigir verificação de telefone via WhatsApp (todos os usuários)</span>
          <Switch className="shrink-0" checked={phone} disabled={loading || saving} onCheckedChange={(v) => saveFlags(intl, br, v)} data-testid="switch-require-phone" />
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm">Mostrar "Criar conta grátis" no site (fillsign.com.br)</span>
          <Switch className="shrink-0" checked={showSignup} disabled={loading || saving} onCheckedChange={(v) => saveFlags(intl, br, phone, v)} data-testid="switch-show-free-signup" />
        </div>
      </CardContent>
    </Card>
  );
}

export default function SystemSettingsPage() {
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const { user, isLoading: authLoading } = useAuth();
  const [, navigate] = useLocation();
  
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [grantAdminEmail, setGrantAdminEmail] = useState("");
  const [grantDialogOpen, setGrantDialogOpen] = useState(false);
  const [revokeAdminId, setRevokeAdminId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCompany, setExpandedCompany] = useState<string | null>(null);
  const [companyMembers, setCompanyMembers] = useState<any[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [deleteCompanyId, setDeleteCompanyId] = useState<string | null>(null);
  const [deleteCompanyName, setDeleteCompanyName] = useState("");
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignCompanyId, setAssignCompanyId] = useState<string | null>(null);
  const [assignEmail, setAssignEmail] = useState("");
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserName, setNewUserName] = useState("");
  const [newUserLastName, setNewUserLastName] = useState("");
  const [newUserPlan, setNewUserPlan] = useState("free");

  // Fetch platform stats
  const { data: stats, isLoading: statsLoading } = useQuery<PlatformStats>({
    queryKey: ["/api/admin/stats"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/admin/stats");
      if (!res.ok) throw new Error("Failed to fetch stats");
      return res.json();
    },
    enabled: !!user,
  });

  // Fetch plans
  const { data: plans, isLoading: plansLoading } = useQuery<Plan[]>({
    queryKey: ["/api/admin/plans"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/admin/plans");
      if (!res.ok) throw new Error("Failed to fetch plans");
      return res.json();
    },
    enabled: !!user,
  });

  // Fetch companies
  const { data: companies, isLoading: companiesLoading } = useQuery<Company[]>({
    queryKey: ["/api/admin/companies"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/admin/companies");
      if (!res.ok) throw new Error("Failed to fetch companies");
      return res.json();
    },
    enabled: !!user,
  });

  // Fetch free users
  const { data: freeUsers, isLoading: freeUsersLoading } = useQuery<FreeUser[]>({
    queryKey: ["/api/admin/free-users"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/admin/free-users");
      if (!res.ok) throw new Error("Failed to fetch free users");
      return res.json();
    },
    enabled: !!user,
  });

  // Fetch all users
  const { data: allUsers, isLoading: allUsersLoading } = useQuery<AllUser[]>({
    queryKey: ["/api/admin/users"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/admin/users");
      if (!res.ok) throw new Error("Failed to fetch users");
      return res.json();
    },
    enabled: !!user,
  });

  // Fetch super admins
  const { data: superAdmins, isLoading: adminsLoading } = useQuery<SuperAdmin[]>({
    queryKey: ["/api/admin/super-admins"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/admin/super-admins");
      if (!res.ok) throw new Error("Failed to fetch admins");
      return res.json();
    },
    enabled: !!user,
  });

  // Fetch all API keys
  const { data: allApiKeys, isLoading: apiKeysLoading } = useQuery<any[]>({
    queryKey: ["/api/admin/api-keys"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/admin/api-keys");
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!user,
  });

  const revokeApiKey = async (keyId: string) => {
    try {
      const res = await apiRequest("POST", `/api/admin/api-keys/${keyId}/revoke`);
      if (res.ok) {
        toast({ title: t("ui.api_key_revoked") });
        queryClient.invalidateQueries({ queryKey: ["/api/admin/api-keys"] });
      }
    } catch { toast({ title: t("common.error"), variant: "destructive" }); }
  };

  // Update plan mutation
  const updatePlanMutation = useMutation({
    mutationFn: async (plan: Plan) => {
      const res = await apiRequest("PUT", `/api/admin/plans/${plan.id}`, plan);
      if (!res.ok) throw new Error("Failed to update plan");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/plans"] });
      setEditDialogOpen(false);
      setEditingPlan(null);
      toast({ title: t("systemSettings.planUpdated") });
    },
    onError: () => {
      toast({ title: t("common.error"), variant: "destructive" });
    },
  });

  // Grant super admin mutation
  const grantAdminMutation = useMutation({
    mutationFn: async (email: string) => {
      const res = await apiRequest("POST", "/api/admin/grant-super-admin", { email });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Failed to grant access");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/super-admins"] });
      setGrantDialogOpen(false);
      setGrantAdminEmail("");
      toast({ title: t("systemSettings.accessGranted") });
    },
    onError: (error: Error) => {
      toast({ title: error.message, variant: "destructive" });
    },
  });

  // Revoke super admin mutation
  const revokeAdminMutation = useMutation({
    mutationFn: async (userId: string) => {
      const res = await apiRequest("POST", `/api/admin/revoke-super-admin/${userId}`);
      if (!res.ok) throw new Error("Failed to revoke access");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/super-admins"] });
      setRevokeAdminId(null);
      toast({ title: t("systemSettings.accessRevoked") });
    },
    onError: () => {
      toast({ title: t("common.error"), variant: "destructive" });
    },
  });

  const handleEditPlan = (plan: Plan) => {
    setEditingPlan({ ...plan });
    setEditDialogOpen(true);
  };

  const handleSavePlan = () => {
    if (editingPlan) {
      updatePlanMutation.mutate(editingPlan);
    }
  };

  const filteredCompanies = companies?.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredFreeUsers = freeUsers?.filter(u => 
    u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    `${u.firstName} ${u.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredAllUsers = allUsers?.filter(u => 
    u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    `${u.firstName} ${u.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleCompanyActive = async (companyId: string, isActive: boolean) => {
    try {
      const res = await apiRequest("POST", `/api/admin/companies/${companyId}/toggle-active`, { isActive, reason: isActive ? null : "Deactivated by admin" });
      if (res.ok) {
        toast({ title: isActive ? "Company activated" : "Company deactivated" });
        queryClient.invalidateQueries({ queryKey: ["/api/admin/companies"] });
      }
    } catch { toast({ title: t("common.error"), variant: "destructive" }); }
  };

  const toggleUserActive = async (userId: string, isActive: boolean) => {
    try {
      const res = await apiRequest("POST", `/api/admin/users/${userId}/toggle-active`, { isActive });
      if (res.ok) {
        toast({ title: isActive ? "User enabled" : "User disabled" });
        queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      }
    } catch { toast({ title: t("common.error"), variant: "destructive" }); }
  };

  const [userToRemove, setUserToRemove] = useState<AllUser | null>(null);
  const [removingUser, setRemovingUser] = useState(false);
  const removeUser = async () => {
    if (!userToRemove) return;
    setRemovingUser(true);
    try {
      const res = await apiRequest("DELETE", `/api/admin/users/${userToRemove.id}`);
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        toast({ title: body.mode === "deleted" ? "Usuário excluído" : "Usuário removido (dados anonimizados, documentos mantidos)" });
        queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
        setUserToRemove(null);
      } else {
        toast({ title: t("common.error"), description: body.message, variant: "destructive" });
      }
    } catch { toast({ title: t("common.error"), variant: "destructive" }); }
    finally { setRemovingUser(false); }
  };

  const changeUserPlan = async (userId: string, plan: string) => {
    try {
      const res = await apiRequest("PATCH", `/api/admin/users/${userId}/plan`, { plan });
      if (res.ok) {
        toast({ title: `Plan changed to ${plan}` });
        queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
        queryClient.invalidateQueries({ queryKey: ["/api/admin/free-users"] });
      } else {
        const data = await res.json();
        toast({ title: data.message || "Error", variant: "destructive" });
      }
    } catch { toast({ title: t("common.error"), variant: "destructive" }); }
  };

  const createUser = async () => {
    if (!newUserEmail.trim() || !newUserName.trim()) return;
    try {
      const res = await apiRequest("POST", "/api/admin/users/create", {
        email: newUserEmail.trim(), firstName: newUserName.trim(), lastName: newUserLastName.trim(), plan: newUserPlan
      });
      if (res.ok) {
        const data = await res.json();
        toast({ title: "User created", description: `Temp password: ${data.temporaryPassword}` });
        queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
        setCreateUserOpen(false);
        setNewUserEmail(""); setNewUserName(""); setNewUserLastName(""); setNewUserPlan("free");
      } else {
        const data = await res.json();
        toast({ title: data.message || "Error", variant: "destructive" });
      }
    } catch { toast({ title: t("common.error"), variant: "destructive" }); }
  };

  const updateCompanyUserLimit = async (companyId: string, userLimit: number) => {
    try {
      const res = await apiRequest("PATCH", `/api/admin/companies/${companyId}/user-limit`, { userLimit });
      if (res.ok) {
        toast({ title: `User limit updated to ${userLimit}` });
        queryClient.invalidateQueries({ queryKey: ["/api/admin/companies"] });
      }
    } catch { toast({ title: t("common.error"), variant: "destructive" }); }
  };

  // Fetch orphaned users (no company)
  const { data: orphanedUsers } = useQuery<any[]>({
    queryKey: ["/api/admin/orphaned-users"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/admin/orphaned-users");
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!user,
  });

  const loadCompanyMembers = async (companyId: string) => {
    if (expandedCompany === companyId) { setExpandedCompany(null); return; }
    setExpandedCompany(companyId);
    setMembersLoading(true);
    try {
      const res = await apiRequest("GET", `/api/admin/companies/${companyId}/members`);
      if (res.ok) setCompanyMembers(await res.json());
    } catch { /* ignore */ }
    setMembersLoading(false);
  };

  const deleteCompany = async () => {
    if (!deleteCompanyId) return;
    try {
      const res = await apiRequest("DELETE", `/api/admin/companies/${deleteCompanyId}`);
      if (res.ok) {
        const data = await res.json();
        toast({ title: t("ui.company_deleted"), description: `${data.orphanedUsers} users are now without a company.` });
        queryClient.invalidateQueries({ queryKey: ["/api/admin/companies"] });
        queryClient.invalidateQueries({ queryKey: ["/api/admin/orphaned-users"] });
      }
    } catch { toast({ title: t("common.error"), variant: "destructive" }); }
    setDeleteCompanyId(null);
  };

  const assignUserToCompany = async () => {
    if (!assignCompanyId || !assignEmail.trim()) return;
    try {
      const res = await apiRequest("POST", `/api/admin/companies/${assignCompanyId}/assign-user`, { email: assignEmail.trim() });
      if (res.ok) {
        toast({ title: "User assigned to company" });
        queryClient.invalidateQueries({ queryKey: ["/api/admin/companies"] });
        queryClient.invalidateQueries({ queryKey: ["/api/admin/orphaned-users"] });
        setAssignDialogOpen(false);
        setAssignEmail("");
        if (expandedCompany === assignCompanyId) loadCompanyMembers(assignCompanyId);
      } else {
        const data = await res.json();
        toast({ title: data.message || "Error", variant: "destructive" });
      }
    } catch { toast({ title: t("common.error"), variant: "destructive" }); }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Redirect to="/login" />;
  }

  if (!user.isSuperAdmin) {
    return <Redirect to="/dashboard" />;
  }

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  return (
    <AppLayout user={user} onLogout={handleLogout} plan={user.plan} currentPage="/admin">
    <div className="space-y-6">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
          <Settings className="h-7 w-7 sm:h-8 sm:w-8 shrink-0" />
          {t("systemSettings.title")}
        </h1>
        <p className="text-muted-foreground mt-1">{t("systemSettings.subtitle")}</p>
      </div>

      {/* Platform Stats Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="hidden sm:block p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">{t("systemSettings.totalUsers")}</p>
                <p className="text-2xl font-bold">{stats?.totalUsers || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="hidden sm:block p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Building2 className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">{t("systemSettings.totalCompanies")}</p>
                <p className="text-2xl font-bold">{stats?.totalCompanies || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="hidden sm:block p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <FileText className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">{t("systemSettings.totalDocuments")}</p>
                <p className="text-2xl font-bold">{stats?.totalDocuments || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="hidden sm:block p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <BarChart3 className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">{t("systemSettings.documentsThisMonth")}</p>
                <p className="text-2xl font-bold">{stats?.documentsThisMonth || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="plans" className="w-full">
        {/* Phones: one scrollable row (the 6-column grid stacked the labels on top of each other). */}
        <TabsList className="flex w-full h-auto justify-start overflow-x-auto md:grid md:grid-cols-6">
          <TabsTrigger value="plans" className="flex items-center gap-2 shrink-0 whitespace-nowrap">
            <CreditCard className="h-4 w-4" />
            {t("systemSettings.plans")}
          </TabsTrigger>
          <TabsTrigger value="companies" className="flex items-center gap-2 shrink-0 whitespace-nowrap">
            <Building2 className="h-4 w-4" />
            {t("systemSettings.companies")}
          </TabsTrigger>
          <TabsTrigger value="all-users" className="flex items-center gap-2 shrink-0 whitespace-nowrap">
            <Users className="h-4 w-4" />
            {t("ui.all_users")}
          </TabsTrigger>
          <TabsTrigger value="free-users" className="flex items-center gap-2 shrink-0 whitespace-nowrap">
            <Users className="h-4 w-4" />
            {t("systemSettings.freeUsers")}
          </TabsTrigger>
          <TabsTrigger value="admins" className="flex items-center gap-2 shrink-0 whitespace-nowrap">
            <Shield className="h-4 w-4" />
            {t("systemSettings.adminAccess")}
          </TabsTrigger>
          <TabsTrigger value="api-keys" className="flex items-center gap-2 shrink-0 whitespace-nowrap">
            <CreditCard className="h-4 w-4" />
            {t("ui.api_keys")}
          </TabsTrigger>
        </TabsList>

        {/* Plans Tab */}
        <TabsContent value="plans">
          <SignupSettingsCard />
          <StripeStatusCard />
          <WhatsAppAgentCard />
          <Card>
            <CardHeader>
              <CardTitle>{t("systemSettings.plans")}</CardTitle>
              <CardDescription>{t("systemSettings.plansDesc")}</CardDescription>
            </CardHeader>
            <CardContent>
              {plansLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : (
                <div className="grid gap-4">
                  {plans?.map((plan) => (
                    <Card key={plan.id} className="border">
                      <CardContent className="pt-6">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <h3 className="text-lg font-semibold">{plan.name}</h3>
                              <Badge variant={plan.isActive ? "default" : "secondary"}>
                                {plan.isActive ? "Active" : "Inactive"}
                              </Badge>
                              {plan.requiresCompany && (
                                <Badge variant="outline">
                                  <Building2 className="h-3 w-3 mr-1" />
                                  {t("ui.requires_company")}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mb-3">{plan.descriptionEn || plan.descriptionPt}</p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <span className="text-muted-foreground">Docs:</span>
                                <span className="ml-2 font-medium">{plan.documentLimit === -1 ? "∞" : plan.documentLimit}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Users:</span>
                                <span className="ml-2 font-medium">{plan.userLimit === -1 ? "∞" : plan.userLimit}</span>
                              </div>
                              <div data-testid={`plan-ai-${plan.planType}`}>
                                <span className="text-muted-foreground">IA/mês:</span>
                                <span className="ml-2 font-medium">{plan.aiAdjustmentsPerMonth ?? 0}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">BRL:</span>
                                <span className="ml-2 font-medium">
                                  R$ {(plan.priceMonthlyBrl ?? 0).toFixed(2)}/mês
                                </span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">USD:</span>
                                <span className="ml-2 font-medium">
                                  $ {(plan.priceMonthlyUsd ?? 0).toFixed(2)}/mo
                                </span>
                              </div>
                            </div>
                            {plan.features && (() => {
                              try {
                                const feats = typeof plan.features === 'string' ? JSON.parse(plan.features) : plan.features;
                                return Array.isArray(feats) && feats.length > 0 ? (
                                  <div className="mt-3 flex flex-wrap gap-1">
                                    {feats.slice(0, 6).map((f: any, idx: number) => (
                                      <Badge key={idx} variant="secondary" className="text-xs">{typeof f === 'string' ? f : f.en || f.pt}</Badge>
                                    ))}
                                    {feats.length > 6 && <Badge variant="outline" className="text-xs">+{feats.length - 6}</Badge>}
                                  </div>
                                ) : null;
                              } catch { return null; }
                            })()}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditPlan(plan)}
                          >
                            <Edit2 className="h-4 w-4 mr-1" />
                            {t("common.edit")}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Companies Tab */}
        <TabsContent value="companies">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <CardTitle>{t("systemSettings.companies")} ({companies?.length || 0})</CardTitle>
                    <CardDescription>{t("systemSettings.companiesDesc")}</CardDescription>
                  </div>
                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder={t("common.search")} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {companiesLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin" /></div>
                ) : filteredCompanies && filteredCompanies.length > 0 ? (
                  <div className="space-y-2">
                    {filteredCompanies.map((company) => (
                      <div key={company.id} className={`rounded-lg border ${company.isActive ? "" : "opacity-60 bg-destructive/5"}`}>
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4">
                          <div className="flex items-center gap-3 cursor-pointer flex-1 min-w-0" onClick={() => loadCompanyMembers(company.id)}>
                            <Building2 className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-medium">{company.name}</span>
                                <Badge variant="outline" className="text-xs">{company.plan}</Badge>
                                <Badge variant={company.isActive ? "default" : "destructive"} className="text-xs">
                                  {company.isActive ? "Active" : "Deactivated"}
                                </Badge>
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {company.country} • {company.memberCount}/{company.userLimit === -1 ? "∞" : company.userLimit} members •{" "}
                                <select className="inline bg-transparent border-b border-dashed border-muted-foreground/50 text-xs cursor-pointer px-0.5"
                                  value={company.userLimit}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => { e.stopPropagation(); updateCompanyUserLimit(company.id, parseInt(e.target.value)); }}>
                                  {[3, 5, 10, 15, 25, 50, 100, -1].map(v => (
                                    <option key={v} value={v}>{v === -1 ? "Unlimited" : `Max ${v}`}</option>
                                  ))}
                                </select>
                                {" "}• {format(new Date(company.createdAt), "MMM d, yyyy")}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0 pl-8 sm:pl-0">
                            <Button variant="ghost" size="sm" title={t("ui.add_user")} onClick={() => { setAssignCompanyId(company.id); setAssignDialogOpen(true); }}>
                              <UserPlus className="h-4 w-4" />
                            </Button>
                            <Button variant={company.isActive ? "outline" : "default"} size="sm" onClick={() => toggleCompanyActive(company.id, !company.isActive)}>
                              {company.isActive ? "Deactivate" : "Activate"}
                            </Button>
                            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => { setDeleteCompanyId(company.id); setDeleteCompanyName(company.name); }}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        {expandedCompany === company.id && (
                          <div className="border-t px-4 py-3 bg-muted/30">
                            {membersLoading ? (
                              <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin" /></div>
                            ) : companyMembers.length > 0 ? (
                              <div className="space-y-2">
                                <p className="text-xs font-medium text-muted-foreground mb-2">Members ({companyMembers.length})</p>
                                {companyMembers.map((m: any) => (
                                  <div key={m.id} className="flex items-center justify-between py-1.5 text-sm">
                                    <div className="flex items-center gap-2">
                                      <Users className="h-3.5 w-3.5 text-muted-foreground" />
                                      <span>{m.firstName} {m.lastName}</span>
                                      <span className="text-muted-foreground">({m.email})</span>
                                    </div>
                                    <Badge variant="secondary" className="text-xs">{m.role}</Badge>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground text-center py-2">No members</p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{t("systemSettings.noCompanies")}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Orphaned Users */}
            {orphanedUsers && orphanedUsers.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-500" />
                    Users without company ({orphanedUsers.length})
                  </CardTitle>
                  <CardDescription>{t("ui.these_users_are_not_linked_to_any_company_you_ca")}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {orphanedUsers.map((u: any) => (
                      <div key={u.id} className="flex items-center justify-between p-3 rounded-lg border">
                        <div>
                          <span className="font-medium text-sm">{u.firstName} {u.lastName}</span>
                          <span className="text-muted-foreground text-sm ml-2">({u.email})</span>
                          <Badge variant="outline" className="ml-2 text-xs">{u.plan}</Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          {companies && companies.length > 0 && (
                            <select
                              className="text-xs border rounded px-2 py-1 bg-background"
                              defaultValue=""
                              onChange={async (e) => {
                                if (!e.target.value) return;
                                try {
                                  const res = await apiRequest("POST", `/api/admin/companies/${e.target.value}/assign-user`, { email: u.email });
                                  if (res.ok) {
                                    toast({ title: "User assigned" });
                                    queryClient.invalidateQueries({ queryKey: ["/api/admin/companies"] });
                                    queryClient.invalidateQueries({ queryKey: ["/api/admin/orphaned-users"] });
                                  }
                                } catch { toast({ title: t("common.error"), variant: "destructive" }); }
                              }}
                            >
                              <option value="">{t("ui.assign_to")}</option>
                              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Delete Company Dialog */}
          <Dialog open={!!deleteCompanyId} onOpenChange={() => setDeleteCompanyId(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-destructive">
                  <Trash2 className="h-5 w-5" /> Delete Company
                </DialogTitle>
                <DialogDescription>
                  This action is irreversible. The company <strong>{deleteCompanyName}</strong> will be permanently deleted.
                </DialogDescription>
              </DialogHeader>
              <div className="p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm space-y-2">
                <p className="font-medium text-amber-800 dark:text-amber-200">What happens when you delete a company:</p>
                <p className="text-amber-700 dark:text-amber-300">• All users linked to this company will become orphaned (without company)</p>
                <p className="text-amber-700 dark:text-amber-300">• Orphaned users will appear in the section above so you can reassign them to another company</p>
                <p className="text-amber-700 dark:text-amber-300">• Documents created by members will remain accessible</p>
                <p className="font-medium text-amber-800 dark:text-amber-200 mt-2">Recommendation: prefer deactivating instead of deleting.</p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDeleteCompanyId(null)}>{t("ui.cancel")}</Button>
                <Button variant="destructive" onClick={deleteCompany}>{t("ui.delete_permanently")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Assign User Dialog */}
          <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5" /> {t("ui.assign_user_to_company")}</DialogTitle>
                <DialogDescription>{t("ui.enter_the_email_of_the_user_you_want_to_add_to_t")}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label>{t("ui.email")}</Label>
                  <Input value={assignEmail} onChange={(e) => setAssignEmail(e.target.value)} placeholder="user@example.com" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setAssignDialogOpen(false); setAssignEmail(""); }}>{t("ui.cancel")}</Button>
                <Button onClick={assignUserToCompany} disabled={!assignEmail.trim()}>{t("ui.assign")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* All Users Tab */}
        <TabsContent value="all-users">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle>{t("ui.all_users")} ({allUsers?.length || 0})</CardTitle>
                  <CardDescription>{t("ui.all_registered_users_on_the_platform")}</CardDescription>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" onClick={() => setCreateUserOpen(true)}>
                    <UserPlus className="h-4 w-4 mr-1" /> {t("systemSettings.createUser")}
                  </Button>
                  <div className="relative flex-1 min-w-[12rem] sm:flex-none sm:w-56">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder={t("common.search")} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {allUsersLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin" /></div>
              ) : filteredAllUsers && filteredAllUsers.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("ui.name")}</TableHead>
                      <TableHead>{t("ui.email")}</TableHead>
                      <TableHead>{t("ui.company")}</TableHead>
                      <TableHead>{t("ui.plan")}</TableHead>
                      <TableHead>{t("ui.docs")}</TableHead>
                      <TableHead>{t("ui.role")}</TableHead>
                      <TableHead>{t("ui.status")}</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAllUsers.map((u) => (
                      <TableRow key={u.id} className={u.isDisabled ? "opacity-50" : ""}>
                        <TableCell className="font-medium">{u.firstName} {u.lastName}</TableCell>
                        <TableCell className="text-xs">{u.email}</TableCell>
                        <TableCell>
                          {u.companyName ? (
                            <div>
                              <span className="text-xs font-medium">{u.companyName}</span>
                              {u.companyRole && <span className="text-xs text-muted-foreground ml-1">({u.companyRole})</span>}
                            </div>
                          ) : <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell>
                          <select className="text-xs border rounded px-1.5 py-0.5 bg-background" value={u.plan}
                            onChange={(e) => changeUserPlan(u.id, e.target.value)}>
                            <option value="free">{t("ui.free")}</option>
                            <option value="pro">{t("ui.pro")}</option>
                            <option value="enterprise">{t("ui.enterprise")}</option>
                            <option value="custom_enterprise">{t("ui.custom")}</option>
                          </select>
                        </TableCell>
                        <TableCell className="text-xs">{u.documentsCount}</TableCell>
                        <TableCell>
                          {u.isSuperAdmin ? <Badge variant="destructive" className="text-xs">Super Admin</Badge> : <Badge variant="secondary" className="text-xs">{u.role}</Badge>}
                        </TableCell>
                        <TableCell>
                          <Badge variant={u.isDisabled ? "destructive" : "default"} className="text-xs">
                            {u.isDisabled ? "Disabled" : "Active"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {!u.isSuperAdmin && (
                            <div className="flex items-center gap-1">
                              <Button variant={u.isDisabled ? "default" : "outline"} size="sm" className="text-xs h-7"
                                onClick={() => toggleUserActive(u.id, u.isDisabled)} data-testid={`button-toggle-user-${u.id}`}>
                                {u.isDisabled ? "Enable" : "Disable"}
                              </Button>
                              <Button variant="ghost" size="sm" className="text-xs h-7 text-destructive hover:text-destructive"
                                onClick={() => setUserToRemove(u)} data-testid={`button-remove-user-${u.id}`}>
                                <Trash2 className="h-3.5 w-3.5 mr-1" />Remover
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{t("ui.no_users_found")}</p>
                </div>
              )}
              <AlertDialog open={!!userToRemove} onOpenChange={(o) => { if (!o) setUserToRemove(null); }}>
                <AlertDialogContent data-testid="dialog-remove-user">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remover usuário?</AlertDialogTitle>
                    <AlertDialogDescription>
                      {userToRemove && (userToRemove.documentsCount > 0
                        ? `${userToRemove.email} tem ${userToRemove.documentsCount} documento(s). A conta é desativada e os dados pessoais são anonimizados; os documentos, assinaturas e auditoria continuam na empresa. Não dá para desfazer.`
                        : `${userToRemove.email} não tem documentos. A conta é apagada de vez (acessos, chaves de API e notificações vão junto). Não dá para desfazer.`)}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={removingUser}>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={(e) => { e.preventDefault(); void removeUser(); }} disabled={removingUser} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="button-confirm-remove-user">
                      {removingUser ? "Removendo..." : "Remover"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Free Users Tab */}
        <TabsContent value="free-users">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle>{t("systemSettings.freeUsers")}</CardTitle>
                  <CardDescription>{t("systemSettings.freeUsersDesc")}</CardDescription>
                </div>
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={t("common.search")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {freeUsersLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : filteredFreeUsers && filteredFreeUsers.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("ui.name")}</TableHead>
                      <TableHead>{t("ui.email")}</TableHead>
                      <TableHead>{t("systemSettings.documentsUsed")}</TableHead>
                      <TableHead>{t("ui.created")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredFreeUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">
                          {user.firstName} {user.lastName}
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <span className={user.documentsUsed >= 3 ? "text-red-600 font-medium" : ""}>
                            {user.documentsUsed} / 3
                          </span>
                        </TableCell>
                        <TableCell>
                          {format(new Date(user.createdAt), "MMM d, yyyy")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{t("systemSettings.noFreeUsers")}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Admin Access Tab */}
        <TabsContent value="admins">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle>{t("systemSettings.adminAccess")}</CardTitle>
                  <CardDescription>{t("systemSettings.adminAccessDesc")}</CardDescription>
                </div>
                <Button className="w-full sm:w-auto" onClick={() => setGrantDialogOpen(true)}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  {t("systemSettings.grantAccess")}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {adminsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : superAdmins && superAdmins.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("ui.name")}</TableHead>
                      <TableHead>{t("ui.email")}</TableHead>
                      <TableHead>{t("ui.granted")}</TableHead>
                      <TableHead>{t("ui.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {superAdmins.map((admin) => (
                      <TableRow key={admin.id}>
                        <TableCell className="font-medium">
                          {admin.firstName} {admin.lastName}
                        </TableCell>
                        <TableCell>{admin.email}</TableCell>
                        <TableCell>
                          {format(new Date(admin.grantedAt), "MMM d, yyyy")}
                        </TableCell>
                        <TableCell>
                          {admin.id !== user?.id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setRevokeAdminId(admin.id)}
                            >
                              <UserMinus className="h-4 w-4 mr-1" />
                              {t("systemSettings.revokeAccess")}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{t("ui.no_super_admins_found")}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* API Keys Tab */}
        <TabsContent value="api-keys">
          <Card>
            <CardHeader>
              <CardTitle>{t("ui.api_keys")} ({allApiKeys?.length || 0})</CardTitle>
              <CardDescription>{t("ui.all_api_keys_across_all_users_and_companies")}</CardDescription>
            </CardHeader>
            <CardContent>
              {apiKeysLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin" /></div>
              ) : allApiKeys && allApiKeys.length > 0 ? (
                <div className="space-y-2">
                  {allApiKeys.map((k: any) => (
                    <div key={k.id} className={`flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between p-3 rounded-lg border ${!k.isActive ? "opacity-50 bg-destructive/5" : ""}`}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <CreditCard className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm">{k.name}</span>
                            <code className="text-xs text-muted-foreground font-mono">{k.keyPrefix}...</code>
                            {k.isDynamics && <Badge variant="outline" className="text-xs">D365</Badge>}
                            {k.companyName && <Badge variant="secondary" className="text-xs">{k.companyName}</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {k.userName} ({k.userEmail})
                            {k.lastUsedAt ? ` • Last used: ${new Date(k.lastUsedAt).toLocaleDateString()}` : " • Never used"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 pl-7 sm:pl-0">
                        <Badge variant={k.isActive ? "default" : "destructive"} className="text-xs">
                          {k.isActive ? "Active" : "Revoked"}
                        </Badge>
                        {k.isActive && (
                          <Button variant="ghost" size="sm" onClick={() => { if (confirm("Revoke this API key?")) revokeApiKey(k.id); }}>
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <CreditCard className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p>{t("ui.no_api_keys_created_yet")}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Plan Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("systemSettings.editPlan")}</DialogTitle>
          </DialogHeader>
          {editingPlan && (
            <div className="space-y-4">
              <div>
                <Label>{t("systemSettings.planName")}</Label>
                <Input
                  value={editingPlan.name}
                  onChange={(e) => setEditingPlan({ ...editingPlan, name: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t("ui.description_pt")}</Label>
                  <Textarea
                    value={editingPlan.descriptionPt || ""}
                    onChange={(e) => setEditingPlan({ ...editingPlan, descriptionPt: e.target.value })}
                  />
                </div>
                <div>
                  <Label>{t("ui.description_en")}</Label>
                  <Textarea
                    value={editingPlan.descriptionEn || ""}
                    onChange={(e) => setEditingPlan({ ...editingPlan, descriptionEn: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t("systemSettings.documentLimit")}</Label>
                  <Input
                    type="number"
                    value={editingPlan.documentLimit}
                    onChange={(e) => setEditingPlan({ ...editingPlan, documentLimit: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label>{t("ui.user_limit")}</Label>
                  <Input
                    type="number"
                    value={editingPlan.userLimit}
                    onChange={(e) => setEditingPlan({ ...editingPlan, userLimit: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="col-span-2">
                  <Label>{language === "en" ? "AI adjustments per month (model library)" : "Ajustes com IA por mês (biblioteca de modelos)"}</Label>
                  <Input
                    type="number" min={0} max={1000}
                    value={editingPlan.aiAdjustmentsPerMonth ?? 0}
                    onChange={(e) => setEditingPlan({ ...editingPlan, aiAdjustmentsPerMonth: Math.max(0, Math.min(1000, parseInt(e.target.value) || 0)) })}
                    data-testid="plan-ai-adjustments"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">{language === "en" ? "Per user. 0 = the option shows locked. Only successful adjustments count; super admins have no limit." : "Por usuário. 0 = a opção aparece bloqueada. Só contam ajustes que deram certo; super admin não tem limite."}</p>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <Label>{t("ui.limit_message_pt")}</Label>
                  <Textarea
                    rows={2}
                    placeholder="Você atingiu o limite mensal de {limit} documentos. Faça upgrade do plano para continuar."
                    value={editingPlan.limitMessagePt || ""}
                    onChange={(e) => setEditingPlan({ ...editingPlan, limitMessagePt: e.target.value })}
                  />
                </div>
                <div>
                  <Label>{t("ui.limit_message_en")}</Label>
                  <Textarea
                    rows={2}
                    placeholder="You have reached your monthly limit of {limit} documents. Please upgrade your plan to continue."
                    value={editingPlan.limitMessageEn || ""}
                    onChange={(e) => setEditingPlan({ ...editingPlan, limitMessageEn: e.target.value })}
                  />
                </div>
                <p className="text-xs text-muted-foreground">{"Tokens disponíveis: {limit} e {current}"}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t("ui.monthly_brl_r")}</Label>
                  <Input
                    type="number" step="0.01"
                    value={editingPlan.priceMonthlyBrl}
                    onChange={(e) => setEditingPlan({ ...editingPlan, priceMonthlyBrl: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label>{t("ui.monthly_usd")}</Label>
                  <Input
                    type="number" step="0.01"
                    value={editingPlan.priceMonthlyUsd}
                    onChange={(e) => setEditingPlan({ ...editingPlan, priceMonthlyUsd: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t("ui.annual_brl_r")}</Label>
                  <Input
                    type="number" step="0.01"
                    value={editingPlan.priceAnnualBrl}
                    onChange={(e) => setEditingPlan({ ...editingPlan, priceAnnualBrl: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label>{t("ui.annual_usd")}</Label>
                  <Input
                    type="number" step="0.01"
                    value={editingPlan.priceAnnualUsd}
                    onChange={(e) => setEditingPlan({ ...editingPlan, priceAnnualUsd: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>ID de preço do Stripe (mensal)</Label>
                  <Input
                    placeholder="price_1Q…"
                    value={editingPlan.stripePriceIdMonthly ?? ""}
                    onChange={(e) => setEditingPlan({ ...editingPlan, stripePriceIdMonthly: e.target.value.trim() })}
                    data-testid="input-stripe-price-monthly"
                  />
                </div>
                <div>
                  <Label>ID de preço do Stripe (anual)</Label>
                  <Input
                    placeholder="price_1Q…"
                    value={editingPlan.stripePriceIdAnnual ?? ""}
                    onChange={(e) => setEditingPlan({ ...editingPlan, stripePriceIdAnnual: e.target.value.trim() })}
                    data-testid="input-stripe-price-annual"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <Switch checked={editingPlan.isActive} onCheckedChange={(checked) => setEditingPlan({ ...editingPlan, isActive: checked })} />
                  <Label>{t("systemSettings.isActive")}</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={editingPlan.requiresCompany} onCheckedChange={(checked) => setEditingPlan({ ...editingPlan, requiresCompany: checked })} />
                  <Label>{t("systemSettings.requiresCompany")}</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={editingPlan.popular || false} onCheckedChange={(checked) => setEditingPlan({ ...editingPlan, popular: checked })} />
                  <Label>{t("ui.popular")}</Label>
                </div>
              </div>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <Switch checked={editingPlan.includesApi || false} onCheckedChange={(checked) => setEditingPlan({ ...editingPlan, includesApi: checked })} />
                  <Label>API</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={editingPlan.includesDynamics || false} onCheckedChange={(checked) => setEditingPlan({ ...editingPlan, includesDynamics: checked })} />
                  <Label>D365</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={editingPlan.includesSso || false} onCheckedChange={(checked) => setEditingPlan({ ...editingPlan, includesSso: checked })} />
                  <Label>SSO</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={editingPlan.includesBranding || false} onCheckedChange={(checked) => setEditingPlan({ ...editingPlan, includesBranding: checked })} />
                  <Label>{t("ui.branding")}</Label>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleSavePlan} disabled={updatePlanMutation.isPending}>
              {updatePlanMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grant Admin Dialog */}
      <Dialog open={grantDialogOpen} onOpenChange={setGrantDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("systemSettings.grantAccess")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t("systemSettings.enterEmail")}</Label>
              <Input
                type="email"
                value={grantAdminEmail}
                onChange={(e) => setGrantAdminEmail(e.target.value)}
                placeholder="email@example.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrantDialogOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button 
              onClick={() => grantAdminMutation.mutate(grantAdminEmail)} 
              disabled={grantAdminMutation.isPending || !grantAdminEmail}
            >
              {grantAdminMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t("systemSettings.grantAccess")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke Admin Confirmation Dialog */}
      <Dialog open={!!revokeAdminId} onOpenChange={() => setRevokeAdminId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("systemSettings.revokeAccess")}</DialogTitle>
            <DialogDescription>
              {t("systemSettings.confirmRevoke")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeAdminId(null)}>
              {t("common.cancel")}
            </Button>
            <Button 
              variant="destructive"
              onClick={() => revokeAdminId && revokeAdminMutation.mutate(revokeAdminId)} 
              disabled={revokeAdminMutation.isPending}
            >
              {revokeAdminMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t("systemSettings.revokeAccess")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createUserOpen} onOpenChange={setCreateUserOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("ui.create_new_user")}</DialogTitle>
            <DialogDescription>{t("ui.create_a_user_account_a_temporary_password_will")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t("ui.email_85")}</Label>
              <Input type="email" value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} placeholder="user@email.com" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("ui.first_name_98")}</Label>
                <Input value={newUserName} onChange={(e) => setNewUserName(e.target.value)} placeholder="John" />
              </div>
              <div>
                <Label>{t("ui.last_name")}</Label>
                <Input value={newUserLastName} onChange={(e) => setNewUserLastName(e.target.value)} placeholder="Doe" />
              </div>
            </div>
            <div>
              <Label>{t("ui.plan")}</Label>
              <select className="w-full border rounded px-3 py-2 bg-background text-sm" value={newUserPlan} onChange={(e) => setNewUserPlan(e.target.value)}>
                <option value="free">{t("ui.free")}</option>
                <option value="pro">{t("ui.pro")}</option>
                <option value="enterprise">{t("ui.enterprise")}</option>
                <option value="custom_enterprise">Custom Enterprise</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateUserOpen(false)}>{t("ui.cancel")}</Button>
            <Button onClick={createUser} disabled={!newUserEmail.trim() || !newUserName.trim()}>
              <UserPlus className="h-4 w-4 mr-1" /> Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </AppLayout>
  );
}
